/**
 * 内容识别填充：wasm PatchMatch 的 TS 侧封装（M4-11 / DESIGN.md §2.11）。
 *
 * 数值权威在 `crates/core/src/patchmatch.rs`（多分辨率金字塔 + 传播/随机搜索，
 * 单元测试在 Rust 侧跑）。这一层只做：wasm 初始化、参数校验、AbortSignal
 * 检查——与 `engine/filters/index.ts` 的 `gaussianBlur` 同一套约定（wasm 调用
 * 本身不可中途取消，只在调用前后检查 `signal`，这是本仓库对"耗时操作接受
 * AbortSignal"这条规则的既有解法，不是本文件独有的妥协）。
 */
import {
  content_aware_fill_rgba8,
  patch_match_valid_source_count,
} from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';

export interface ContentAwareFillOptions {
  readonly signal?: AbortSignal;
  /** 随机种子——同输入同种子结果确定；换种子换一种填充结果。缺省 1。 */
  readonly seed?: number;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/** mask 里 255 的像素会被替换为从 mask 为 0 的区域采样的内容。 */
export async function contentAwareFill(
  pixels: Uint8Array,
  mask: Uint8Array,
  width: number,
  height: number,
  options: ContentAwareFillOptions = {},
): Promise<Uint8Array> {
  if (pixels.length !== width * height * 4) {
    throw new RangeError(`像素缓冲长度与 ${width}×${height} 不符。`);
  }
  if (mask.length !== width * height) {
    throw new RangeError(`mask 长度与 ${width}×${height} 不符。`);
  }
  throwIfAborted(options.signal);
  await ensureKernel();
  throwIfAborted(options.signal);
  const result = content_aware_fill_rgba8(pixels, mask, width, height, options.seed ?? 1);
  throwIfAborted(options.signal);
  return result;
}

/**
 * 选区能否被内容识别填充——0 表示图里没有任何完整的已知 patch（洞占满
 * 整张图/贴着好几条边），调用方据此提前给用户一个"选区太大，填不出来"的
 * 提示，而不是跑一轮 PatchMatch 之后发现啥都没填。
 */
export async function contentAwareFillableSourceCount(
  mask: Uint8Array,
  width: number,
  height: number,
  options: ContentAwareFillOptions = {},
): Promise<number> {
  if (mask.length !== width * height) {
    throw new RangeError(`mask 长度与 ${width}×${height} 不符。`);
  }
  throwIfAborted(options.signal);
  await ensureKernel();
  throwIfAborted(options.signal);
  return patch_match_valid_source_count(mask, width, height);
}
